// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Game sequencing (M2-14, M2-15, M2-17, M2-18, M2-19).
//
// The layer between "a ball is bouncing around" and "somebody is playing pinball": whose turn
// it is, which ball they are on, whether the drain counted, what the bonus came to, and
// whether the score was good enough to put initials on.
//
// **All of it is in the core, and that is not obvious.** It would be natural to treat player
// rotation and initials entry as user-interface concerns. They are not, because a leaderboard
// score is validated by re-simulating the player's input trace on a server with no interface
// at all. Anything that can change the score has to be here, deterministic and step-counted,
// or the server and the client will disagree about what the game was. That includes the bonus
// count-up, the match, and the tilt penalty.
//
// The engine, the tilt bob and the ball search are all owned here rather than talking to each
// other, because each of them needs to be testable without the others.

import { World } from '../sim/World'
import { restoreWorld, snapshotWorld } from '../sim/snapshot'
import type { WorldSnapshot } from '../sim/snapshot'
import { createGameRng, type Rng } from '../rng/Rng'
import { BALL_RADIUS, STEP_DT, STEP_HZ } from '../sim/constants'
import { roundHalfAwayFromZero } from '../math/deterministic'
import { RuleEngine, msToSteps } from '../rules/RuleEngine'
import type { RuleEffect, RuleEngineState } from '../rules/RuleEngine'
import { Kicker } from '../sim/devices'
import { SwitchMatrix } from './SwitchMatrix'
import { CONTACTS_TO_TILT, TiltBob } from './TiltBob'
import type { TiltBobState, TiltEvent } from './TiltBob'
import { BallSearch } from './BallSearch'
import type { BallSearchState } from './BallSearch'
import type { MachineDefinition } from '../machine/types'
import { resolveSettings, settingFlag, settingNumber } from '../machine/settings'
import type { ResolvedSettings } from '../machine/settings'

/**
 * Cabinet switches, which belong to the machine rather than to any device on the playfield.
 *
 * A real machine has these on its coin door and its flipper buttons, and its ruleset wires
 * them by name like any other switch. They are conventions rather than configuration because
 * every machine has them, and a definition that renamed them would be describing different
 * hardware. A definition that does not declare one simply never sees it.
 */
export const SW_BALL_DRAINED = 'ballDrained'
export const SW_FLIPPER_LEFT_BUTTON = 'flipperLeftButton'
export const SW_FLIPPER_RIGHT_BUTTON = 'flipperRightButton'

/** What the one launch button did, since it is three buttons wired together. */
export type LaunchOutcome = 'plunged' | 'started' | 'committed' | 'nothing'

export type GamePhase =
  | 'attract'
  | 'ballInPlay'
  | 'bonus'
  | 'betweenBalls'
  | 'gameOver'
  | 'highScoreEntry'

export interface PlayerState {
  readonly index: number
  score: number
  ballsPlayed: number
  extraBalls: number
  /** Tilted on the ball currently in play. Cleared when the next ball starts. */
  tilted: boolean
}

export interface HighScoreRow {
  readonly initials: string
  readonly score: number
}

/** Classic three-character entry. `<` is rubout, as on a real machine. */
export const INITIALS_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 <'

/**
 * Where the three characters start when nothing else is asked for.
 *
 * **The trace contract's default, and therefore load-bearing.** `ReplayTrace.startingInitials` is
 * optional, and *absent means this* — which is what lets every trace recorded before the field
 * existed re-simulate to the identical three characters on a build that has it.
 */
export const DEFAULT_INITIALS = 'AAA'

/**
 * What the table says for a player who was asked and declined (M2-19a).
 *
 * **In the core because it lands on a leaderboard row**, exactly like the characters a player does
 * type: a server re-simulating the trace has to reach the same string from the same input, and a
 * marker invented by whichever client happened to draw the row would be a value the validator
 * could not reproduce. It matches the client's own marker for a score that never qualified,
 * because to a reader they are the same fact — a score with no name on it.
 */
export const SKIPPED_INITIALS = '---'

/**
 * Three characters as indices into `INITIALS_ALPHABET`, for the wheels to open on.
 *
 * **Total, never throwing, and never refusing.** Short, long, lower-case, a character this
 * machine's alphabet does not have, or nothing at all — every one of them resolves to a position,
 * because the value's whole job is to save a returning player six button presses and *nothing*
 * about where the wheels start is worth failing initials entry over. Anything unrecognised falls
 * back to the first letter, which is exactly where the wheels used to open for everybody.
 *
 * Upper-cased first: the alphabet is upper-case and a browser holding `mjh` from a hand-edited
 * key would otherwise land on three A's, which reads as the feature having quietly stopped.
 */
function initialsIndices(text: string | undefined): readonly [number, number, number] {
  const at = (i: number): number => {
    const found = INITIALS_ALPHABET.indexOf((text ?? '').toUpperCase()[i] ?? '')
    // `<` is rubout — a control, not a character — so it is not a place to *start*.
    return found < 0 || INITIALS_ALPHABET[found] === '<' ? 0 : found
  }
  return [at(0), at(1), at(2)]
}

/**
 * The gap before the **first** bonus digit is awarded, in steps.
 *
 * 05 §5.11: *"an audible tick per increment, **accelerating**"*, and until M9-44 the ladder was
 * flat at 140. It starts slow now and closes up, which is what a real machine does — the ladder
 * is the drama, and a bonus that ticks at a constant rate is a number being read out.
 *
 * **This was written once before, measured, and taken back out**, because it is not a tuning
 * change: the count-up runs for the sum of its gaps, `finishBall` is called from *inside* the last
 * tick, and the number of ticks varies with the bonus — so no shrinking ladder ends the ball on
 * the same step a flat one does for every bonus. **A trace is indexed by step**, so every recorded
 * input after that point lands somewhere else in the next ball and the game becomes a different
 * game: the hundred recorded replays came back with different scores, `game-000` at 2,782,000
 * against a recorded 3,584,500, from a change that awards exactly the same total.
 *
 * That is what `CORE_VERSION` is for, and it moved to `3` with this. The hundred games were
 * re-recorded against the new core in the same change.
 */
export const BONUS_TICK_STEPS = 240

/**
 * How each gap shrinks, as a **rational** rather than a multiplier.
 *
 * `gap = floor(gap × 4 ÷ 5)` — four fifths, computed in integers. A `0.8` would be a binary64
 * literal that is not exactly four fifths, and while the two cores would agree about it today,
 * the core's own rules exist to stop that class of question arising at all: this way C#'s integer
 * division and TypeScript's `Math.floor` of the same product are the same number by construction,
 * with nothing to round.
 */
export const BONUS_TICK_SHRINK_NUMERATOR = 4
export const BONUS_TICK_SHRINK_DENOMINATOR = 5

/**
 * The fastest the ladder gets, in steps.
 *
 * 45 ms between chimes is about as close as two of them can be and still be heard as two. Without
 * a floor the geometric shrink reaches one step and the tail of a large bonus arrives as a single
 * noise — which is the opposite of what the count-up is for.
 */
export const BONUS_TICK_FLOOR_STEPS = 45

/** The seed a game uses when the host does not name one. */
export const DEFAULT_GAME_SEED = 20260817

export interface GameOptions {
  readonly playerCount?: number
  readonly ballsPerGame?: number
  /** Seeds the match number. Kept apart from the physics stream on purpose. */
  readonly seed?: number
  /** Where a served ball appears. Defaults to the plunger or the first kicker found. */
  readonly serveAt?: readonly [number, number]
  /** Below this a ball has drained. Defaults to just under the apron. */
  readonly drainY?: number
  readonly highScores?: readonly HighScoreRow[]
  /**
   * The three characters initials entry opens on. Defaults to `DEFAULT_INITIALS`.
   *
   * **An input, not a memory** (M2-19a). The player's last-used initials live in the browser, and
   * the core has no storage, no clock and no way to read one — so the remembered value arrives the
   * way the seed and the player count do: named by the host at construction and written into the
   * trace, where a validator re-simulating the game finds the same starting point and reaches the
   * same row. A core that read this for itself would produce a leaderboard row no replay could
   * reproduce, which is the one thing initials entry lives in here to prevent.
   *
   * Anything not in `INITIALS_ALPHABET` is taken as the first letter rather than refused: this is
   * a convenience about where the wheels start, and a stored value from an older build, a hand-
   * edited one, or one from a machine whose alphabet has changed must never be able to stop a
   * player entering their initials.
   */
  readonly startingInitials?: string
}

export class Game {
  readonly definition: MachineDefinition
  readonly world: World
  readonly engine: RuleEngine
  readonly tilt = new TiltBob()
  readonly search: BallSearch
  /** The wiring loom: physics events in, coil firings out. */
  readonly matrix: SwitchMatrix

  readonly players: PlayerState[] = []
  readonly ballsPerGame: number

  /**
   * The operator menu, resolved (04 §15).
   *
   * **Read for the first time in M9-34.** The block has been validated by both cores since M2 and
   * folded into the leaderboard key since M4-19 — so two players on different settings have always
   * ranked separately, on a difference that could not exist. Held on the game rather than looked up
   * per use so there is one resolution per game, which is also what makes it a thing a snapshot
   * does not have to carry: it is a property of the *definition*, and a validator re-simulating
   * this trace loads the same bytes.
   */
  readonly settings: ResolvedSettings

  /** Whether an `extraBall` effect awards one (04 §15's `extraBallEnabled`). */
  private readonly extraBallEnabled: boolean

  phase: GamePhase = 'attract'
  currentPlayer = 0
  /** 1-based, as a player counts them. */
  currentBall = 1
  step = 0

  /** Steps of ball save remaining. Zero when the ball is not saved. */
  ballSaveSteps = 0
  /** Versioned rules opt in; absent on old definitions/snapshots preserves their replays. */
  ballSaveAllBalls = false

  /** Below this the ball has left the playfield. A little under the apron, in metres. */
  drainY: number
  /** Balls sitting in a lock, waiting for a multiball to release them. */
  ballsLocked = 0
  /** Coils a rule fired that nothing is wired to. A rulesheet bug, reported not swallowed. */
  readonly unwiredCoils: string[] = []
  /** Where each ball left the playfield. The soak classifies drains from this. */
  readonly drainedAt: [number, number][] = []
  /** Credits from match and replay. */
  credits = 0
  matchDigits = -1
  knockerFired = 0

  readonly highScores: HighScoreRow[]
  /** Indices into `INITIALS_ALPHABET`, one per character position. */
  readonly initials: number[] = [0, 0, 0]
  /** Where those three start, as indices. See `GameOptions.startingInitials`. */
  readonly startingInitials: readonly [number, number, number]
  initialsPosition = 0
  /** Player whose initials are being taken, or −1. */
  initialsFor = -1

  /** Diagnostics and presentation cues drained by the host. */
  readonly effects: RuleEffect[] = []
  readonly log: string[] = []

  private readonly rng: Rng
  private readonly serveX: number
  private readonly serveY: number
  /** The kicker sitting at the serve point, if the machine has one. */
  private readonly launcher: Kicker | undefined
  private readonly manualPlungers: readonly Kicker[]
  private readonly replayScore: number
  private replayAwarded = false
  private bonusRemaining = 0
  private bonusPerTick = 0
  private bonusNextTick = 0

  /**
   * The gap the count-up is currently running at, in steps (M9-44).
   *
   * **Carried in state and snapshotted**, not recomputed from the tick number, because a resume
   * lands mid-count-up and a ladder rebuilt from scratch there would slow back down in the middle
   * of a bonus. This repository has the lesson from `Game.rng`: *a snapshot that carries
   * everything is tested at boundaries where the missing thing is still at rest* — every existing
   * boundary is before the bonus, so nothing would have caught it.
   */
  private bonusTickGap = BONUS_TICK_STEPS
  /**
   * The cue a tick plays, or nothing (04 §12's `scoring.bonus.countUpSound`).
   *
   * **Declared in the format since M2, validated by both cores, and read by nobody** — so 05 §5.11's
   * *"audible tick per increment"* was a silent count-up on every surface of the product. Resolved
   * once in the constructor rather than looked up per tick: it is a property of the definition, and
   * the count-up runs at up to twenty ticks a second.
   */
  private readonly bonusTickSound: string | undefined
  private readonly coilPulses: string[] = []
  /** Balls the auto-plunger still has to send up the lane. */
  private autoLaunches = 0
  private autoLaunchMask = 0
  private readonly trackAutoLaunchByBall: boolean

  /**
   * Steps a ball has sat in the shooter lane with nobody plunging it (FR-003).
   *
   * A real machine has an auto-plunger coil and a timer, and it fires because a machine that can
   * be left in a state a player cannot get out of is a machine that eats coins. It matters more
   * here than on a real one: in a headset the plunger is a knob you have to *find*, and a player
   * who has not found it yet has no way at all to start the ball (06 §2.2).
   *
   * Counted in **steps, not milliseconds**, like every other timer in the core — so it survives
   * a snapshot, re-simulates identically in the validator, and cannot depend on a frame rate.
   */
  private idleInLaneSteps = 0
  /** How long that may go on before the auto-plunger fires. Steps. Read from the machine. */
  private readonly autoPlungeSteps: number
  /** Which cabinet button drives each bat, by index into `world.flippers`. */
  private readonly buttonOfFlipper: ('left' | 'right')[] = []

  /**
   * Who has already put initials on the table this game.
   *
   * A plain array of booleans indexed by player, not a set: simulation state is iterated in a
   * fixed order and a server re-simulating this has to reach the same answer.
   */
  private readonly initialsTaken: boolean[] = []

  constructor(definition: MachineDefinition, world: World, options: GameOptions = {}) {
    this.definition = definition
    this.world = world
    const playerCount = options.playerCount ?? 1
    // **The operator menu, before anything that reads it** (04 §15, M9-34). Resolved from the
    // definition alone: no client offers a chooser yet, so every game runs at the machine's
    // declared defaults and a validator re-simulating this trace loads the same bytes and reaches
    // the same numbers. *When a chooser is built the trace has to carry the chosen values in the
    // same change*, or a game played on a non-default machine is a game the server re-simulates as
    // a different one — which is what `HttpReplaySubmitter` already says about the settings hash,
    // and the reason there is no `GameOptions.settings` here waiting to be misused.
    this.settings = resolveSettings(definition)
    this.ballsPerGame =
      options.ballsPerGame ?? settingNumber(this.settings, 'ballsPerGame', definition.meta.ballsPerGame)
    this.extraBallEnabled = settingFlag(this.settings, 'extraBallEnabled', true)
    // Zero warnings is legal — §15's range is [0, 4] — and means the first contact tilts. Clamped
    // at one contact, because a bob that needs no contacts to tilt has tilted before the game
    // starts.
    this.tilt.contactsToTilt = Math.max(
      1,
      roundHalfAwayFromZero(settingNumber(this.settings, 'tiltWarnings', CONTACTS_TO_TILT - 1)) + 1,
    )
    this.engine = new RuleEngine(definition, { playerCount })
    this.trackAutoLaunchByBall = hasAllBallSave(definition)
    this.rng = createGameRng(options.seed ?? DEFAULT_GAME_SEED)
    this.highScores = [...(options.highScores ?? [])]
    this.startingInitials = initialsIndices(options.startingInitials)
    // The operator's replay score wins over the rulesheet's, for the reason above: a replay is an
    // award the *machine* makes, and 04 §15 puts it in the operator menu. NEON NOVA types
    // 25,000,000 in both places.
    this.replayScore = settingNumber(
      this.settings,
      'replayScore',
      definition.rules?.scoring?.replayScore ?? Number.POSITIVE_INFINITY,
    )
    this.autoPlungeSteps = autoPlungeStepsFor(definition)
    this.bonusTickSound = definition.rules?.scoring?.bonus?.countUpSound

    for (let i = 0; i < playerCount; i++) {
      this.players.push({ index: i, score: 0, ballsPlayed: 0, extraBalls: 0, tilted: false })
      this.initialsTaken.push(false)
    }

    const serve = options.serveAt ?? defaultServePoint(definition)
    this.serveX = serve[0]
    this.serveY = serve[1]
    this.drainY = options.drainY ?? -definition.dimensions.playfield.length / 2 - 0.02

    // Identified by position rather than by id, so a machine whose plunger is called anything
    // at all still launches.
    this.launcher = world.devices.find(
      (d): d is Kicker =>
        d instanceof Kicker &&
        Math.abs(d.cx - this.serveX) < 1e-9 &&
        Math.abs(d.cy - this.serveY) < 1e-9,
    )

    this.search = new BallSearch(world, definition)
    this.search.troughX = this.serveX
    this.search.troughY = this.serveY

    // Built from the world rather than handed in by the loader, so a game constructed from a
    // definition and a hand-built world is wired identically to one that came through
    // `loadMachine`. There is one wiring loom, not two.
    const byId = new Map<string, (typeof world.devices)[number]>()
    for (const device of world.devices) byId.set(device.id, device)
    this.matrix = new SwitchMatrix(definition, { devices: byId })
    // Definition order resolves simultaneous manual holds. Automatic feeds retain their
    // serve-point launcher and can never consume a launch on an unrelated captured ball.
    this.manualPlungers = definition.devices.flatMap((device) => {
      const runtime = byId.get(device.id)
      return device.kind === 'plunger' && runtime instanceof Kicker && runtime !== this.launcher
        ? [runtime] : []
    })

    // A cabinet has two flipper buttons and every bat is wired to one of them, so the wiring
    // is a property of the machine and belongs here — a replay records *button* presses, and
    // a server re-simulating it has to reach the same bats the player's browser did. Left to
    // the host, the wiring would be a table the client and the validator each kept their own
    // copy of, and a divergence between them would look like a physics bug.
    const declared = definition.devices.filter((d) => d.kind === 'flipper')
    for (let i = 0; i < world.flippers.length; i++) {
      // Positional, because `loadMachine` builds bats in definition order. A hand-built world
      // may not correspond at all, so the bat's own rotation direction is the fallback.
      const d = declared.length === world.flippers.length ? declared[i] : undefined
      this.buttonOfFlipper.push(
        d !== undefined && d.kind === 'flipper'
          ? d.handedness
          : world.flippers[i]!.direction > 0
            ? 'left'
            : 'right',
      )
    }
  }

  // --- input ---------------------------------------------------------------------
  //
  // The flipper buttons go through here rather than straight to the bats, because they are
  // *switches*. NEON NOVA rotates the lit skill-shot lane on a button press before the ball
  // has even been plunged, and a rule can only see that if the press reaches the matrix.

  pressFlipper(side: 'left' | 'right' | 'upper', down: boolean): void {
    for (let i = 0; i < this.world.flippers.length; i++) {
      const flipper = this.world.flippers[i]!
      const isLeft = flipper.direction > 0
      if (side === 'upper') {
        if (i < 2) continue
      } else if (side === 'left' ? !isLeft : isLeft) {
        continue
      } else if (i >= 2) {
        continue
      }
      if (!this.players[this.currentPlayer]!.tilted) flipper.setPressed(down, this.step)
    }
    this.closeButtonSwitch(side === 'upper' ? 'left' : side, down)
  }

  /**
   * Press a cabinet flipper button, driving every bat wired to it.
   *
   * This is what a player actually touches, and it is not the same thing as `pressFlipper`.
   * NEON NOVA's third bat is an upper-*left* flipper: on a real machine it is on the left
   * button, and a host that drove only the lower pair would leave a third of the shot
   * vocabulary — the whole left orbit feed — physically unreachable while every test of the
   * bat itself passed.
   *
   * `pressFlipper` stays, because a test that wants one specific bat should not have to know
   * the wiring, and a machine with a third button addresses its mini-playfield bat directly.
   */
  pressButton(side: 'left' | 'right', down: boolean): void {
    // During initials entry the buttons are not flipper buttons. This lives here rather than in
    // the host because **a replay has no host**: the initials a player enters end up on a
    // leaderboard row, and a server re-simulating the input trace has to reach the same three
    // characters from the same button presses. Anything about what a button does that lives
    // above the core is a thing the validator cannot reproduce.
    if (down && this.phase === 'highScoreEntry') {
      if (side === 'left') this.initialsPrevious()
      else this.initialsNext()
      return
    }
    const tilted = this.players[this.currentPlayer]!.tilted
    for (let i = 0; i < this.world.flippers.length; i++) {
      if (this.buttonOfFlipper[i] !== side) continue
      if (!tilted) this.world.flippers[i]!.setPressed(down, this.step)
    }
    this.closeButtonSwitch(side, down)
  }

  /**
   * Press the launch button — which is three buttons wired to one, as on a real cabinet.
   *
   * A plunger when a ball is waiting, the start button when the game is over or in attract, and
   * the enter key during initials entry. Which one it is depends on what the machine is doing,
   * and the player has only ever had one button in front of them.
   *
   * **Here rather than in the host, for the same reason as the flipper buttons above.** This was
   * `PlaySession.pressLaunch` in the client: correct, tested, and unreachable from a server that
   * has a `Game` and an input trace and nothing else. A replay validator re-simulating "the
   * launch button was pressed at step 41,200" could not have known whether that started a game,
   * fired a plunger or committed a letter.
   */
  /**
   * Whether the launch button, pressed now, would begin a game.
   *
   * The same three questions `pressLaunchButton` asks, in the same order, without acting on
   * them. A session needs the answer *before* the press: a game that begins has to begin on the
   * pristine simulation — step 0, the seed's own random stream, every device at rest — because
   * that is the game a validator constructs to re-simulate its trace, and once the press has
   * answered 'started' the game it started on is already gone.
   */
  launchWouldStart(): boolean {
    if (this.phase === 'highScoreEntry') return false
    if (this.canManualLaunch) return false
    return this.phase === 'attract' || this.isOver
  }

  pressLaunchButton(power = 1): LaunchOutcome {
    if (this.phase === 'highScoreEntry') {
      this.initialsCommit()
      return 'committed'
    }
    const manual = this.manualLauncher()
    if (manual !== undefined) {
      manual.eject(this.world, power)
      return 'plunged'
    }
    if (this.phase === 'attract' || this.isOver) {
      this.startGame()
      return 'started'
    }
    return 'nothing'
  }

  /**
   * Close the button's switch on the press, never on the release.
   *
   * A rule watching `flipperLeftButton` is watching for a *press* — NEON NOVA rotates the lit
   * skill-shot lane on one — so closing on the release too would step the lane twice per tap.
   * The bats themselves still see both edges; they have to, or they would never come back down.
   */
  private closeButtonSwitch(side: 'left' | 'right', down: boolean): void {
    if (!down) return
    const id = side === 'right' ? SW_FLIPPER_RIGHT_BUTTON : SW_FLIPPER_LEFT_BUTTON
    if (this.matrix.has(id)) this.engine.closeSwitch(id)
  }

  /**
   * The whole simulation, at a step boundary (M4-28a).
   *
   * The prerequisite M0-17 surfaced: validating a two-hour replay is 7.2 million steps, past what
   * one Edge Function invocation may spend, so a long game is validated in chunks — and a chunk
   * boundary is worth nothing unless the simulation can be put down and picked up bit for bit.
   *
   * Everything is here, not only the world: the score, the bonus count-down mid-tick, which
   * player is on which ball, what the tilt bob is doing, how long each stuck-ball timer has run,
   * and every rule variable. A snapshot that carried the world alone would resume a game whose
   * physics was right and whose score was zero.
   *
   * **Only at a step boundary.** The rule engine's pending triggers and its `closedNow` set are
   * cleared within a step, so neither is carried — and taking a snapshot part way through
   * `advance()` would silently drop them. `SwitchMatrix.closedThisStep` is not carried either: it
   * is rewritten from scratch at the top of the next pump, so a resumed game has it right again
   * one step later.
   *
   * **`effects`, `log`, `drainedAt` and `unwiredCoils` are deliberately absent, and that is a
   * decision rather than an oversight.** All four are *output*: cues a host drains, an unbounded
   * diagnostic, a list the soak classifies, and a rulesheet complaint. None is read by the
   * simulation, so none changes what happens next — and carrying them would restore cues a host
   * has already consumed and hand them over a second time. `test/core/snapshot.test.ts` names
   * every exclusion, so a fifth one cannot be added quietly.
   */
  snapshot(): GameSnapshot {
    return {
      world: snapshotWorld(this.world),
      engine: this.engine.snapshot(),
      tilt: this.tilt.snapshot(),
      search: this.search.snapshot(),
      phase: this.phase,
      currentPlayer: this.currentPlayer,
      currentBall: this.currentBall,
      step: this.step,
      ballSaveSteps: this.ballSaveSteps,
      ballSaveAllBalls: this.ballSaveAllBalls,
      idleInLaneSteps: this.idleInLaneSteps,
      ballsLocked: this.ballsLocked,
      credits: this.credits,
      matchDigits: this.matchDigits,
      knockerFired: this.knockerFired,
      initials: [...this.initials],
      initialsPosition: this.initialsPosition,
      initialsFor: this.initialsFor,
      initialsTaken: [...this.initialsTaken],
      replayAwarded: this.replayAwarded,
      bonusRemaining: this.bonusRemaining,
      bonusPerTick: this.bonusPerTick,
      bonusNextTick: this.bonusNextTick,
      bonusTickGap: this.bonusTickGap,
      autoLaunches: this.autoLaunches,
      autoLaunchMask: this.autoLaunchMask,
      players: this.players.map((p) => ({
        index: p.index,
        score: p.score,
        ballsPlayed: p.ballsPlayed,
        extraBalls: p.extraBalls,
        tilted: p.tilted,
      })),
      highScores: this.highScores.map((h) => ({ ...h })),
      rng: this.rng.saveState(),
    }
  }

  /**
   * Put a game back exactly as it was.
   *
   * The game must have been constructed from the same definition, with the same player count and
   * the same seed. Checked rather than assumed: a partial restore produces a game that runs
   * perfectly and is not the one that was saved, and nothing downstream can tell.
   */
  restore(snapshot: GameSnapshot): void {
    if (snapshot.players.length !== this.players.length) {
      throw new Error(
        `Game.restore: ${snapshot.players.length} players in the snapshot, ${this.players.length} in the game`,
      )
    }
    restoreWorld(this.world, snapshot.world)
    this.engine.restore(snapshot.engine)
    this.tilt.restore(snapshot.tilt)
    this.search.restore(snapshot.search)
    this.phase = snapshot.phase
    this.currentPlayer = snapshot.currentPlayer
    this.currentBall = snapshot.currentBall
    this.step = snapshot.step
    this.ballSaveSteps = snapshot.ballSaveSteps
    this.ballSaveAllBalls = snapshot.ballSaveAllBalls ?? false
    this.idleInLaneSteps = snapshot.idleInLaneSteps
    this.ballsLocked = snapshot.ballsLocked
    this.credits = snapshot.credits
    this.matchDigits = snapshot.matchDigits
    this.knockerFired = snapshot.knockerFired
    for (const [i, letter] of snapshot.initials.entries()) this.initials[i] = letter
    this.initialsPosition = snapshot.initialsPosition
    this.initialsFor = snapshot.initialsFor
    for (const [i, taken] of snapshot.initialsTaken.entries()) this.initialsTaken[i] = taken
    this.replayAwarded = snapshot.replayAwarded
    this.bonusRemaining = snapshot.bonusRemaining
    this.bonusPerTick = snapshot.bonusPerTick
    this.bonusNextTick = snapshot.bonusNextTick
    this.bonusTickGap = snapshot.bonusTickGap
    this.autoLaunches = snapshot.autoLaunches
    this.autoLaunchMask = snapshot.autoLaunchMask ?? 0
    for (const [i, p] of snapshot.players.entries()) {
      const player = this.players[i]!
      player.score = p.score
      player.ballsPlayed = p.ballsPlayed
      player.extraBalls = p.extraBalls
      player.tilted = p.tilted
    }
    this.highScores.length = 0
    for (const row of snapshot.highScores) this.highScores.push({ ...row })
    this.rng.loadState(snapshot.rng)
  }

  // --- lifecycle ---------------------------------------------------------------

  startGame(): void {
    for (const p of this.players) {
      p.score = 0
      p.ballsPlayed = 0
      p.extraBalls = 0
      p.tilted = false
    }
    // **A start spends one credit and keeps the rest** (the owner, 26 Sep 2026: coins inserted
    // before a start should carry into the game). It zeroed them, so five coins and a plunge left
    // none, and a replay or match credit won in one game was gone at the next start. Free play: a
    // start with no credit still starts, and spends nothing.
    this.credits = Math.max(0, this.credits - 1)
    this.matchDigits = -1
    this.knockerFired = 0
    this.replayAwarded = false
    this.currentPlayer = 0
    this.currentBall = 1
    this.initialsTaken.fill(false)
    this.tilt.resetGame()
    this.search.reset()
    this.engine.vars.resetGame()
    this.engine.scores.fill(0)
    this.note('game start')
    this.beginBall()
  }

  private beginBall(): void {
    this.phase = 'ballInPlay'
    this.ballSaveAllBalls = false
    if (this.trackAutoLaunchByBall) { this.autoLaunches = 0; this.autoLaunchMask = 0 }
    this.tilt.resetBall()
    this.players[this.currentPlayer]!.tilted = false

    // Hold Bonus (05 section 5.9). The multiplier is a ball-scoped variable, so it is the
    // ball change that clears it and no rule can intercept that — which is why the carry is
    // here rather than in the rulesheet.
    const bonus = this.definition.rules?.scoring?.bonus
    const holdVar = bonus?.holdVar
    const multiplierVar = bonus?.multiplier?.var
    const held =
      holdVar !== undefined && multiplierVar !== undefined &&
      this.engine.vars.get(holdVar, this.currentPlayer) !== 0
        ? this.engine.vars.get(multiplierVar, this.currentPlayer)
        : 0

    this.engine.startBall(this.currentPlayer, this.currentBall)
    if (held > 0 && multiplierVar !== undefined) {
      this.engine.vars.set(multiplierVar, this.currentPlayer, held)
      this.note(`bonus multiplier ${held} held`)
    }
    this.serveBall()
    this.note(`player ${this.currentPlayer + 1}, ball ${this.currentBall}`)
  }

  private serveBall(): number {
    const ball = this.world.balls.find((b) => !b.active)
    if (ball === undefined) return -1
    this.world.spawnBall(ball.id, this.serveX, this.serveY)
    ball.z = BALL_RADIUS
    this.engine.ballsInPlay = this.ballsInPlay()
    return ball.id
  }

  private queueAutoLaunch(ballId: number): void {
    if (!this.trackAutoLaunchByBall) { this.autoLaunches++; return }
    if (ballId < 0) return
    const bit = 1 << ballId
    if ((this.autoLaunchMask & bit) !== 0) return
    this.autoLaunchMask |= bit
    this.autoLaunches++
  }

  private removeAutoLaunch(ballId: number): void {
    if (!this.trackAutoLaunchByBall) return
    const bit = 1 << ballId
    if ((this.autoLaunchMask & bit) === 0) return
    this.autoLaunchMask &= ~bit
    this.autoLaunches--
  }

  private completeAutoFeeds(): void {
    if (!this.trackAutoLaunchByBall || this.autoLaunchMask === 0) return
    const launcher = this.launcher
    const radius = (launcher?.captureRadius ?? 0) + BALL_RADIUS
    for (const ball of this.world.balls) {
      if ((this.autoLaunchMask & (1 << ball.id)) === 0) continue
      const dx = ball.x - this.serveX, dy = ball.y - this.serveY
      // Simultaneous feeds can separate under ball-ball contact and enter play without being
      // caught by the launcher. Such a ball is already fed; do not launch it again on return.
      if (!ball.active || launcher === undefined || (ball.heldBy >= 0 && ball.heldBy !== launcher.index)
        || (ball.heldBy < 0 && dx * dx + dy * dy > radius * radius)) this.removeAutoLaunch(ball.id)
    }
  }

  /**
   * Put one more ball on the playfield, at rest at `x`, `y` — the Builder's test tool (M13-25
   * follow-up, 26 Sep 2026: "a second ball in a test"). Only while a ball is in play, and only into a
   * slot the machine has: false when the game is not in play or every ball the world holds is
   * already out. The rule engine is told the new count exactly as a serve tells it, so a drain of
   * either ball is an ordinary drain of one of two — the last one down ends the ball, as multiball
   * does. **Never reached by play**: no rule, switch or control calls it, and a trace has no action
   * for it; a test session is not a submitted game. `Game.AddBall` is the twin.
   */
  addBall(x: number, y: number): boolean {
    if (this.phase !== 'ballInPlay') return false
    const ball = this.world.balls.find((b) => !b.active)
    if (ball === undefined) return false
    this.world.spawnBall(ball.id, x, y)
    ball.z = BALL_RADIUS
    this.engine.ballsInPlay = this.ballsInPlay()
    this.note('a ball was added by the test tools')
    return true
  }

  /**
   * Fire the plunger, putting the served ball into play.
   *
   * A served ball sits in the shooter lane until the player launches it, and on a machine
   * whose plunger holds indefinitely — `holdMs: -1`, which is what a scoop under rule control
   * wants — nothing else will ever move it. Without this the game serves a ball and waits
   * for ever, which is exactly what the first version did.
   *
   * `power` is how far the plunger was pulled back, 0 … 1, and it is not a nicety. NEON NOVA
   * carries a full skill-shot ladder — rotate the lit lane with either flipper button, four
   * lane awards and a super skill shot on the ramp — and with a single fixed plunge strength
   * not one of them can be collected: every launch leaves at the same speed, skims over the
   * N-O-V-A lanes and carries on round the orbit. Those rules fired in the rule test and were
   * unreachable in the machine, which is the same shape of defect as the flipper hand-off and
   * just as invisible to everything that was being measured.
   *
   * Auto-launch — multiball, ball save — always plunges full: nobody is pulling it.
   */
  launch(power = 1): boolean {
    const device = this.launcher
    if (device === undefined || device.heldBall < 0) return false
    device.eject(this.world, power)
    return true
  }

  /**
   * Steps a ball may wait in the lane before the machine plunges it, or zero for never. Read by a
   * client that pauses a game nobody is playing (1.0.231, after the headset's 1.0.228): a machine
   * that plunges for the player has said a waiting game moves on, so the client leaves it to.
   */
  get idleAutoPlungeSteps(): number {
    return this.autoPlungeSteps
  }

  /** True while a ball is waiting in the shooter lane. */
  get awaitingLaunch(): boolean {
    return (this.launcher?.heldBall ?? -1) >= 0
  }

  /** A served ball first, then one occupied manual plunger in definition order. */
  get canManualLaunch(): boolean {
    return this.manualLauncher() !== undefined
  }

  private manualLauncher(): Kicker | undefined {
    if (this.phase !== 'ballInPlay') return undefined
    if (this.manualBallReady(this.launcher)) return this.launcher
    for (const plunger of this.manualPlungers) if (this.manualBallReady(plunger)) return plunger
    return undefined
  }

  private manualBallReady(plunger: Kicker | undefined): boolean {
    const ball = this.world.balls[plunger?.heldBall ?? -1]
    return ball !== undefined && ball.active && ball.heldBy === plunger!.index
  }

  private ballsInPlay(): number {
    let n = 0
    for (const b of this.world.balls) if (b.active) {
      const holder = b.heldBy >= 0 ? this.world.devices[b.heldBy] : undefined
      if (!(holder instanceof Kicker && holder.physicalLocked)) n++
    }
    return n
  }

  // --- the step ------------------------------------------------------------------

  /**
   * Advance the whole game by one simulation step.
   *
   * Order matters and is fixed: physics, then the tilt bob (which reads the frame the physics
   * just moved), then the rule engine (which reads the switches the physics just closed),
   * then ball search, then the phase machine.
   */
  advance(): void {
    this.step = this.world.stepIndex

    if (this.phase === 'ballInPlay') {
      this.world.step()
      this.step = this.world.stepIndex

      const tiltEvent = this.tilt.advance(this.step, this.world.frameVX, this.world.frameVY, STEP_DT)
      if (tiltEvent !== 'none') this.onTilt(tiltEvent)
      // Slam ended the game: pending saves/feeds must not reactivate its captured balls.
      if (tiltEvent === 'slam') return

      // Drains are collected *before* the rules resolve, so a rule asking `ballsInPlay` on the
      // step a ball was lost gets the answer after the loss. Nova Multiball ends "when one
      // ball remains", and one step of lag there is the difference between ending it and not.
      this.collectDrains()
      this.matrix.pump(this.world, this.engine)
      this.engine.ballsInPlay = this.ballsInPlay()
      this.engine.advance(this.step)
      this.collectEffects()

      // Ball search asks; something has to answer. Until this line existed the first two
      // stages — pulse the coils near the ball, then pulse every coil — did nothing at all,
      // and a ball resting on a bumper skirt escalated straight past them to a free ball.
      this.coilPulses.length = 0
      this.search.advance(this.step, this.coilPulses)
      for (const id of this.coilPulses) this.matrix.pulseDevice(this.world, id)

      if (this.ballSaveSteps > 0) this.ballSaveSteps--
      if (this.ballSaveSteps <= 0) this.ballSaveAllBalls = false
      this.completeAutoFeeds()
      if (this.autoLaunches > 0 && this.awaitingLaunch) {
        const ballId = this.launcher!.heldBall
        if (this.launch()) {
          if (this.trackAutoLaunchByBall) this.removeAutoLaunch(ballId)
          else this.autoLaunches--
        }
      }
      this.tickAutoPlunger()
      this.checkDrain()
      return
    }

    // Off the playfield the simulation still runs, so a ball rolling into the trough during
    // the bonus count keeps moving and the world's step index stays the game's clock.
    this.world.step()
    this.step = this.world.stepIndex

    if (this.phase === 'bonus') this.tickBonus()
  }

  private collectEffects(): void {
    for (const effect of this.engine.drainEffects()) {
      this.effects.push(effect)
      if (effect.kind === 'extraBall') {
        // **The operator can switch extra balls off** (04 §15's `extraBallEnabled`, default true).
        // Noted either way rather than silently dropped: a rulesheet that awards one and a machine
        // that does not give it is a difference a rules author debugging their own table needs to
        // be able to see, and `effects` is already where a host reads what the game did.
        if (this.extraBallEnabled) {
          this.players[this.currentPlayer]!.extraBalls++
          this.note('extra ball awarded')
        } else {
          this.note('extra ball declined — the operator has them switched off')
        }
      } else if (effect.kind === 'ballSave') {
        // **`settings.ballSaveSeconds` is deliberately not read here**, and since M9-35 that is no
        // longer the same as it not being read at all. One operator number cannot mean the five
        // different grants NEON NOVA's rulesheet makes — so the *author* says which one the coin
        // door governs, with `enableBallSave: "operator"`, and `RuleEngine` resolves it there. By
        // the time it reaches this line it is steps, exactly as a literal is.
        this.ballSaveSteps = effect.value
        if (effect.id === 'allBalls') this.ballSaveAllBalls = true
        if (effect.value <= 0) this.ballSaveAllBalls = false
      } else if (effect.kind === 'knocker') {
        this.knockerFired++
      } else if (effect.kind === 'multiball') {
        // The value is how many balls should be in play, not how many to add: a multiball
        // starting from a locked ball plus the one in hand needs one fewer than one starting
        // from a single ball, and a ruleset should not have to know which.
        const need = effect.value - this.ballsInPlay()
        for (let i = 0; i < need; i++) this.queueAutoLaunch(this.serveBall())
        this.ballsLocked = 0
      } else if (effect.kind === 'lockBall') {
        if (effect.value === 1) { this.ballsLocked++; this.note('a virtual ball was locked') }
        else this.lockBall(effect.id)
      } else if (effect.kind === 'coil') {
        if (!this.matrix.fire(this.world, effect)) {
          this.unwiredCoils.push(effect.id)
          this.note(`coil ${effect.id} drives nothing`)
        }
      }
    }
    // The engine's per-player score is the authority; the game mirrors it so a caller has one
    // place to read from and the two can never drift.
    this.players[this.currentPlayer]!.score = this.engine.scores[this.currentPlayer] ?? 0
    this.checkReplay()
  }

  private onTilt(event: TiltEvent): void {
    if (event === 'warning') {
      this.note(`tilt warning ${this.tilt.warnings}`)
      return
    }
    if (event === 'tilt') {
      this.players[this.currentPlayer]!.tilted = true
      // Flippers dead, scoring for the ball forfeited (PH-072). The score already banked
      // stays; it is the bonus that is lost, which is how a real machine punishes a tilt.
      for (const flipper of this.world.flippers) flipper.setPressed(false, this.step)
      this.note('TILT')
      return
    }
    this.note('SLAM TILT — game over')
    this.endGame()
  }

  /**
   * A coin paid into the coin door (06 §2.4).
   *
   * "The coin door is decorative but functional as a gesture: pressing the coin slot adds a credit
   * in free play, with the correct mechanical clunk." The clunk is the client's; the credit is
   * this, and it is in the core rather than beside the gesture for the reason every other control
   * is — **`credits` is inside `fingerprint`**, so a credit added outside the simulation is a
   * checkpoint a validator re-simulating the same trace cannot reproduce.
   *
   * **Capped, because a coin slot is a thing a hand can drum on.** A real machine stops counting
   * too; the number is not what the gesture is for, and an unbounded counter is a fingerprint
   * input a player can drive as far as they like.
   */
  addCredit(): void {
    if (this.credits >= MAX_CREDITS) return
    this.credits++
    this.note('credit added')
  }

  private checkReplay(): void {
    if (this.replayAwarded) return
    if (this.players[this.currentPlayer]!.score < this.replayScore) return
    this.replayAwarded = true
    // Capped like the coin slot, and for the reason `addCredit` gives rather than a new one:
    // `credits` is inside `fingerprint`, so it is bounded on *every* path or it is not bounded.
    // Once per game here, so it has never come near ninety-nine — which is why it went unnoticed
    // and is not why it is right.
    if (this.credits < MAX_CREDITS) this.credits++
    this.knockerFired++
    this.note('replay awarded')
  }

  /**
   * Retire any ball that has left the bottom of the playfield.
   *
   * The simulation has no opinion about drains — a ball below the apron is just a ball with a
   * position. Something has to decide the ball is gone, and it has to be here rather than in
   * the renderer, because a server re-simulating a replay has no renderer and must reach the
   * same conclusion on the same step.
   */
  private collectDrains(): void {
    let lost = 0
    for (const ball of this.world.balls) {
      if (!ball.active || ball.heldBy >= 0) continue
      if (ball.y > this.drainY) continue
      ball.active = false
      this.removeAutoLaunch(ball.id)
      lost++
      this.drainedAt.push([ball.x, ball.y])
      this.note(`ball ${ball.id} drained at x=${ball.x.toFixed(4)}`)
      if (this.matrix.has(SW_BALL_DRAINED)) this.engine.closeSwitch(SW_BALL_DRAINED)
    }
    // Restore every lost ball before rules read ballsInPlay, so the protected drain cannot
    // prematurely end multiball. Collect the whole batch first: feeding into this loop could
    // revisit a newly served slot. The expiry step matches the existing final-ball save.
    if (this.ballSaveAllBalls && this.ballSaveSteps > 1 && !this.players[this.currentPlayer]!.tilted) {
      for (let i = 0; i < lost; i++) { this.queueAutoLaunch(this.serveBall()); this.note('multiball ball saved') }
    }
  }

  /**
   * Take a ball out of play and put another one up, which is what a lock physically is.
   *
   * The ball nearest the named device is the one that went in. Naming the device rather than
   * the ball is deliberate: a rule cannot see ball indices, and should not be able to.
   */
  private lockBall(deviceId: string): void {
    const device = this.world.devices.find((d) => d.id === deviceId)
    if (device instanceof Kicker && device.lockable) {
      if (device.lockHeld()) {
        this.ballsLocked++
        this.note(`physical ball ${device.heldBall} retained in ${deviceId}`)
        this.queueAutoLaunch(this.serveBall())
      }
      return
    }
    const atX = device?.originX ?? 0
    const atY = device?.originY ?? 0
    let chosen = -1
    let nearest = Number.POSITIVE_INFINITY
    for (const ball of this.world.balls) {
      if (!ball.active) continue
      const dx = ball.x - atX
      const dy = ball.y - atY
      const d2 = dx * dx + dy * dy
      if (d2 >= nearest) continue
      nearest = d2
      chosen = ball.id
    }
    if (chosen < 0) return
    const ball = this.world.balls[chosen]!
    ball.active = false
    ball.heldBy = -1
    this.ballsLocked++
    this.note(`ball ${chosen} locked in ${deviceId}`)
    // A real machine kicks a fresh ball into the shooter lane the moment a lock takes one,
    // which is why locking a ball never ends the ball.
    this.queueAutoLaunch(this.serveBall())
  }

  /**
   * Fire the auto-plunger on a ball nobody has plunged (FR-003).
   *
   * The timer runs only while a ball is actually waiting, and is reset the instant one is not —
   * so a player who plunges promptly every time never meets this, and one who puts the headset
   * down mid-ball comes back to a game that has moved on rather than to a machine that stopped.
   *
   * **Deliberately after the multiball feed above.** A multiball feed already has an auto-launch
   * queued and will fire on the very next step; letting this one get there first would spend a
   * queued launch on a ball that was about to be launched anyway, and leave the next feed short.
   */
  private tickAutoPlunger(): void {
    // Off — no timeout stated — the ball waits for the pull, however long.
    if (!this.awaitingLaunch || this.autoPlungeSteps <= 0) {
      this.idleInLaneSteps = 0
      return
    }
    this.idleInLaneSteps++
    if (this.idleInLaneSteps < this.autoPlungeSteps) return
    this.idleInLaneSteps = 0
    // Full strength, because a ball the machine plunges for you must clear the lane. A soft
    // auto-plunge that leaves the ball halfway up the shooter lane is the stuck ball this exists
    // to prevent, arrived at by a longer route.
    if (this.launch(1)) this.note('auto-plunger fired')
  }

  private checkDrain(): void {
    if (this.ballsInPlay() > 0) return

    if (this.ballSaveSteps > 0 && !this.players[this.currentPlayer]!.tilted) {
      this.note('ball saved')
      this.serveBall()
      return
    }
    this.beginBonus()
  }

  // --- end of ball (M2-17) ----------------------------------------------------------

  private beginBonus(): void {
    this.engine.endBall()
    this.engine.advance(this.step)
    this.collectEffects()

    const player = this.players[this.currentPlayer]!
    const bonus = this.definition.rules?.scoring?.bonus
    const heldOnTilt = bonus?.heldOnTilt === true

    if (bonus === undefined || (player.tilted && !heldOnTilt)) {
      if (player.tilted) this.note('bonus lost to tilt')
      this.phase = 'betweenBalls'
      this.finishBall()
      return
    }

    let base = 0
    for (const term of bonus.base ?? []) {
      base += this.engine.vars.get(term.var, this.currentPlayer) * term.perUnit
    }
    const multiplier = bonus.multiplier ? Math.max(1, this.engine.vars.get(bonus.multiplier.var, this.currentPlayer)) : 1

    this.bonusRemaining = base * multiplier
    // The count-up is a real part of the feel — a ladder of chimes, not a single number
    // appearing. Ten ticks, and the accelerating ladder sums to 1,080 steps for a full ten
    // (240, 192, 153, 122, 97, 77, 61, 48, then the 45-step floor twice) — just over a second,
    // which is what a count-up wants to be.
    this.bonusPerTick = this.bonusRemaining > 0 ? Math.ceil(this.bonusRemaining / 10) : 0
    // The ladder starts at its widest and closes up. See `BONUS_TICK_STEPS`.
    this.bonusTickGap = BONUS_TICK_STEPS
    this.bonusNextTick = this.step + this.bonusTickGap
    this.phase = this.bonusRemaining > 0 ? 'bonus' : 'betweenBalls'
    if (this.phase === 'betweenBalls') this.finishBall()
    else this.note(`bonus ${this.bonusRemaining}`)
  }

  private tickBonus(): void {
    if (this.step < this.bonusNextTick) return
    // **Accelerating** (05 §5.11, M9-44): four fifths of the last gap, in integers, down to a
    // floor. Shrunk *before* the next deadline is set, so the gap after this chime is already the
    // shorter one — a ladder that shrank afterwards would play its first two ticks at the same
    // width and start a beat late.
    this.bonusTickGap = Math.max(
      BONUS_TICK_FLOOR_STEPS,
      Math.floor((this.bonusTickGap * BONUS_TICK_SHRINK_NUMERATOR) / BONUS_TICK_SHRINK_DENOMINATOR),
    )
    this.bonusNextTick = this.step + this.bonusTickGap
    const award = Math.min(this.bonusPerTick, this.bonusRemaining)
    this.bonusRemaining -= award
    this.engine.scores[this.currentPlayer] = (this.engine.scores[this.currentPlayer] ?? 0) + award
    this.players[this.currentPlayer]!.score = this.engine.scores[this.currentPlayer]!
    // **The chime, which `countUpSound` has named since M2 and nothing has ever played.** The field
    // is in the format, validated by both cores, and had no reader at all — so 05 §5.11's "audible
    // tick per increment" was a silent count-up on every surface. Pushed as an ordinary `sound`
    // effect, so the host that already drains `effects` for every other cue plays it with no new
    // machinery and a machine that names no cue is silent exactly as it was.
    if (this.bonusTickSound !== undefined) {
      this.effects.push({
        kind: 'sound',
        id: this.bonusTickSound,
        value: 0,
        step: this.step,
      })
    }
    this.checkReplay()
    if (this.bonusRemaining > 0) return
    this.phase = 'betweenBalls'
    this.finishBall()
  }

  // --- sequencing (M2-14, M2-15) --------------------------------------------------------

  private finishBall(): void {
    const player = this.players[this.currentPlayer]!

    // An extra ball replays the same ball number — that is what makes it an extra ball rather
    // than a longer game.
    if (player.extraBalls > 0 && !this.tilt.slammed) {
      player.extraBalls--
      this.note('shoot again')
      this.beginBall()
      return
    }

    player.ballsPlayed++

    const next = this.nextPlayer()
    if (next < 0) {
      this.endGame()
      return
    }
    this.currentPlayer = next
    this.currentBall = this.players[next]!.ballsPlayed + 1
    this.beginBall()
  }

  /**
   * Whose turn is next, or −1 when the game is over.
   *
   * Round-robin from the current player, taking the first who has balls left. Written as a
   * scan rather than as arithmetic because extra balls mean two players can be on different
   * ball numbers, and the modulo version of this is wrong in a way that only shows up in a
   * four-player game where somebody earned an extra ball.
   */
  private nextPlayer(): number {
    const n = this.players.length
    for (let i = 1; i <= n; i++) {
      const candidate = (this.currentPlayer + i) % n
      if (this.players[candidate]!.ballsPlayed < this.ballsPerGame) return candidate
    }
    return -1
  }

  // --- end of game (M2-18, M2-19) ----------------------------------------------------------

  private endGame(): void {
    this.phase = 'gameOver'
    for (const ball of this.world.balls) ball.active = false
    this.note('game over')
    this.runMatch()
    this.openHighScoreEntry()
  }

  /**
   * Match: the last few digits of a score against a number the machine draws.
   *
   * **The draw has to be inside the modulus, and for four milestones it was not.** This drew
   * `nextInt(10) * 10` — 0, 10, … 90 — against `score % 10 ** digits`, which is right for the two
   * digits every real machine matches on and wrong for every other value the format allows. At
   * `digits: 3` the modulus is 1000 and the draw could still only be 0…90, so the machine could
   * match a score ending `000` through `090` and **nothing else**: nine-tenths of the possible
   * endings were unreachable and no player would ever know why they never matched. At `digits: 1`
   * it was worse — `score % 10` is 0…9 and the draw is a multiple of ten, so only a drawn 0 could
   * ever match anything at all.
   *
   * NEON NOVA declares `digits: 2`, which is why this was latent rather than live, and it is
   * exactly the shape this repository keeps recording: *a parameter name is a claim about its
   * argument, and the callers are what say whether the claim is true.* One caller, one value, four
   * milestones.
   *
   * **Round tens above one digit, because that is what makes a match feel close rather than
   * random** — a real machine's wheels stop on a multiple of ten, so a score of 12,345,670 is one
   * digit away from every match and 12,345,673 is not. At one digit there are no tens to be round
   * about: the whole modulus is 0…9, so the draw is simply one of them. `nextInt(10)` at two
   * digits is the identical call it has always made, which is why the hundred recorded games are
   * unmoved.
   */
  private runMatch(): void {
    const match = this.definition.rules?.scoring?.match
    if (match?.enabled !== true) return
    const digits = match.digits ?? 2
    const modulus = 10 ** digits
    this.matchDigits = digits <= 1 ? this.rng.nextInt(10) : this.rng.nextInt(10 ** (digits - 1)) * 10
    for (const player of this.players) {
      if (player.score % modulus !== this.matchDigits) continue
      // **`awardsCredit` is read, and it was not.** 04 §12 offers it and a credit was given
      // regardless, so a machine set to free play knocked and counted anyway. The knocker goes
      // with the credit rather than with the match: on a real machine it is the *credit* knocker,
      // and a machine that awards nothing has nothing to announce.
      if (match.awardsCredit !== false) {
        if (this.credits < MAX_CREDITS) this.credits++
        this.knockerFired++
      }
      this.note(`match — player ${player.index + 1}`)
    }
  }

  /**
   * Ask the next player who qualifies for their initials, if there is one.
   *
   * **`initialsTaken` is what makes this terminate.** Called once from `endGame` and again from
   * `recordHighScore` — "another player may also have qualified" — and without a record of who
   * has already answered, the second call re-selects the player who just did: their score is now
   * *on* the table, and `qualifies` says yes to anything while the table has room. A one-player
   * game therefore asked for initials, accepted them, and asked again, for ever.
   *
   * It never failed a test, because the only games played headless were played by a robot that
   * has no way to enter initials, so every one of them ran out of budget in the same phase and
   * that looked like the game simply taking a while. It was found by playing one in a browser
   * and watching the machine stop.
   */
  private openHighScoreEntry(): void {
    const table = this.definition.rules?.scoring?.highScore?.table ?? 0
    if (table <= 0) return
    for (const player of this.players) {
      if (this.initialsTaken[player.index] === true) continue
      if (!this.qualifies(player.score, table)) continue
      this.initialsFor = player.index
      // Where the host said to start, which is 'AAA' unless it remembered something (M2-19a).
      this.initials[0] = this.startingInitials[0]
      this.initials[1] = this.startingInitials[1]
      this.initials[2] = this.startingInitials[2]
      this.initialsPosition = 0
      this.phase = 'highScoreEntry'
      return
    }
  }

  private qualifies(score: number, table: number): boolean {
    if (score <= 0) return false
    if (this.highScores.length < table) return true
    return score > (this.highScores[table - 1]?.score ?? 0)
  }

  // --- initials entry (M2-19) -----------------------------------------------------------------
  //
  // Flipper-scrolled, exactly as a real machine: left steps back through the alphabet, right
  // steps forward, and the launch button commits the character. It lives in the core because
  // the *initials themselves* end up on a leaderboard row that a replay has to reproduce.

  initialsPrevious(): void {
    if (this.phase !== 'highScoreEntry') return
    const at = this.initials[this.initialsPosition]!
    this.initials[this.initialsPosition] = (at + INITIALS_ALPHABET.length - 1) % INITIALS_ALPHABET.length
  }

  initialsNext(): void {
    if (this.phase !== 'highScoreEntry') return
    const at = this.initials[this.initialsPosition]!
    this.initials[this.initialsPosition] = (at + 1) % INITIALS_ALPHABET.length
  }

  /**
   * Decline the table, and take the score anyway (M2-19a).
   *
   * **A control, so it is here.** A real cabinet has no skip — it has three characters and a
   * player who is going to type them — but this machine is played by somebody on a phone who may
   * have neither the time nor the wish, and a question with no way out is a machine that has
   * stopped. What makes it a *core* concern is the same thing that made the flipper buttons one:
   * it decides what appears on a leaderboard row, and a validator has a `Game` and a trace and
   * nothing else.
   *
   * **The score keeps its place.** Skipping is a decision about a name, not about a score: the
   * player earned the row, so the row is theirs, and it carries `SKIPPED_INITIALS` where the
   * characters would have gone. The alternative — dropping the entry — would quietly punish
   * somebody for not wanting to be named, and it would make the table disagree with the browser's
   * own list, which records the score either way.
   */
  initialsSkip(): void {
    if (this.phase !== 'highScoreEntry') return
    this.recordHighScore(SKIPPED_INITIALS)
  }

  initialsCommit(): void {
    if (this.phase !== 'highScoreEntry') return
    const letter = INITIALS_ALPHABET[this.initials[this.initialsPosition]!]!
    if (letter === '<') {
      // Rubout steps back rather than entering a character, which is what the arrow is for.
      if (this.initialsPosition > 0) {
        this.initialsPosition--
        this.initials[this.initialsPosition] = 0
      }
      return
    }
    this.initialsPosition++
    if (this.initialsPosition < 3) return
    this.recordHighScore()
  }

  get initialsText(): string {
    let out = ''
    for (let i = 0; i < 3; i++) out += INITIALS_ALPHABET[this.initials[i]!]
    return out
  }

  private recordHighScore(initials: string = this.initialsText): void {
    const player = this.players[this.initialsFor]
    // Marked before anything else: this player has answered, whatever happens to the table.
    if (this.initialsFor >= 0) this.initialsTaken[this.initialsFor] = true
    if (player !== undefined) {
      this.highScores.push({ initials, score: player.score })
      // Descending by score; ties keep the earlier entry, which is the convention and also
      // the only stable answer.
      this.highScores.sort((a, b) => b.score - a.score)
      const table = this.definition.rules?.scoring?.highScore?.table ?? 5
      this.highScores.length = Math.min(this.highScores.length, table)
      this.note(`high score ${initials} ${player.score}`)
    }
    this.initialsFor = -1
    // Another player may also have qualified.
    this.phase = 'gameOver'
    this.openHighScoreEntry()
  }

  // --- helpers --------------------------------------------------------------------------------

  get isOver(): boolean {
    return this.phase === 'gameOver' && this.initialsFor < 0
  }

  /** Coil ids ball search asked to be pulsed on the step just completed. */
  get pendingCoilPulses(): readonly string[] {
    return this.coilPulses
  }

  /**
   * Take the presentation cues accumulated since the last call.
   *
   * `effects` was documented as drained by the host and there was no way to drain it, so a
   * host that read it kept every award, sound and display cue of the whole session alive —
   * thousands per game, growing for as long as the machine is switched on. Draining is the
   * host's job because a *replay* has no host and must not be made to have one.
   */
  drainEffects(): RuleEffect[] {
    const out = this.effects.slice()
    this.effects.length = 0
    return out
  }

  private note(message: string): void {
    this.log.push(`${this.step}: ${message}`)
  }

  /** Bit-exact fingerprint of the whole game, for golden transcripts. */
  fingerprint(): number[] {
    const out: number[] = [
      this.currentPlayer,
      this.currentBall,
      this.credits,
      this.matchDigits,
      this.knockerFired,
    ]
    for (const p of this.players) out.push(p.score, p.ballsPlayed, p.extraBalls, p.tilted ? 1 : 0)
    const full = out.concat(this.engine.fingerprint())
    // New state is appended only for opted-in saves; every old definition keeps its exact vector.
    if (this.trackAutoLaunchByBall) full.push(this.ballSaveAllBalls ? 1 : 0, this.ballSaveSteps, this.autoLaunchMask)
    return full
  }
}

/**
 * Where a ball is served from when the caller does not say.
 *
 * A plunger if the machine has one, otherwise any kicker, otherwise the bottom of the
 * playfield. Guessing is only acceptable because it is overridable and because a machine
 * without either is a test rig rather than a machine.
 */
function defaultServePoint(definition: MachineDefinition): readonly [number, number] {
  for (const device of definition.devices) {
    if (device.kind === 'plunger' || device.kind === 'autoPlunger') return device.centre
  }
  for (const device of definition.devices) {
    if (device.kind === 'kicker') return device.centre
  }
  return [0, -definition.dimensions.playfield.length / 2 + 0.05]
}

/** New versioned definitions opt in; old definitions retain their complete feed/replay behavior. */
function hasAllBallSave(definition: MachineDefinition): boolean {
  const actions = [...(definition.rules?.rules ?? []).flatMap(r => r.do),
    ...(definition.rules?.modes ?? []).flatMap(m => [...(m.onStart ?? []), ...(m.onShot ?? []), ...(m.onComplete ?? []), ...(m.onTimeout ?? [])])]
  return actions.some(a => {
    const spec = a['startMultiball']
    return typeof spec === 'object' && spec !== null && (spec as Record<string, unknown>)['saveAllBalls'] === true
  })
}

export { STEP_HZ, msToSteps }

/**
 * A whole game, at a step boundary (M4-28a).
 *
 * Plain data, no classes. It is written to a database column and read back by another process,
 * and anything with behaviour in it would have to be revived — which is where a resumable
 * simulation stops being exactly restorable.
 */
/**
 * The most credits a machine will hold (06 §2.4).
 *
 * Real machines stop counting somewhere; the exact number is an operator setting nobody here has
 * asked for, so this is simply "more than anybody will press and small enough to be a bound".
 */
export const MAX_CREDITS = 99

export interface GameSnapshot {
  readonly world: WorldSnapshot
  readonly engine: RuleEngineState
  readonly tilt: TiltBobState
  readonly search: readonly BallSearchState[]
  readonly phase: GamePhase
  readonly currentPlayer: number
  readonly currentBall: number
  readonly step: number
  readonly ballSaveSteps: number
  readonly ballSaveAllBalls?: boolean
  /** Steps a ball has waited in the shooter lane. Drives the auto-plunger (FR-003). */
  readonly idleInLaneSteps: number
  readonly ballsLocked: number
  readonly credits: number
  readonly matchDigits: number
  readonly knockerFired: number
  readonly initials: readonly number[]
  readonly initialsPosition: number
  readonly initialsFor: number
  readonly initialsTaken: readonly boolean[]
  readonly replayAwarded: boolean
  readonly bonusRemaining: number
  readonly bonusPerTick: number
  readonly bonusNextTick: number
  /** The gap the count-up is running at (M9-44). See `Game.bonusTickGap`. */
  readonly bonusTickGap: number
  readonly autoLaunches: number
  readonly autoLaunchMask?: number
  readonly players: readonly PlayerState[]
  readonly highScores: readonly HighScoreRow[]
  /**
   * The game's own random stream (M8-76).
   *
   * Drawn from exactly once per game, for the match — which is why every boundary the
   * snapshot had ever been tested at agreed with a fresh game: nothing had touched the stream
   * yet. A second game restored from the pristine snapshot without it drew the *second*
   * match number of the seed's stream while a validator's fresh game drew the first, and
   * every checkpoint after game over disagreed with a score that was right (2 Sep 2026).
   */
  readonly rng: readonly [number, number, number, number]
}/**
 * Default seconds a ball may sit in the shooter lane before the auto-plunger fires (FR-003).
 *
 * **Zero: off, unless the machine states a timeout** (14 Sep 2026). It was twelve — "short
 * enough that a player who has walked away … is not left staring at a machine that has stopped"
 * — and from a headset that read as the plunger firing on its own: a player building, placing
 * the machine or looking at the wrist menu spends twelve seconds easily, and the ball left
 * without them. The product owner's rule: "it should only send ball when user pulls plunger". A
 * ball in the lane is not stuck; the knob is there. A machine that wants the old behaviour states
 * `autoPlungeSeconds` in its settings. Multiball feeds are unaffected — they queue their own
 * launches. **Both cores**, or a validated replay diverges on the step the ball left the lane.
 */
export const AUTO_PLUNGE_SECONDS = 0

/** The setting a machine overrides it with. An operator control, so it lives in `settings`. */
export const AUTO_PLUNGE_SETTING = 'autoPlungeSeconds'

/**
 * How long this machine waits, in steps.
 *
 * Read from the definition's own `settings` block rather than from a `GameOptions` field,
 * because FR-003 says *configurable* and an operator setting is what configurable means on a
 * pinball machine — it is already in the format, already carried with the machine, and already
 * hashed into the leaderboard key, so two machines with different timeouts keep separate boards
 * exactly as two machines with different ball counts do.
 *
 * A setting of the wrong shape, or a nonsensical number, falls back to the default rather than
 * throwing. A machine whose operator menu has a typo in it should still be playable, and the
 * validator must reach the same conclusion as the client about a definition neither of them
 * wrote.
 */
function autoPlungeStepsFor(definition: MachineDefinition): number {
  const setting = definition.settings?.[AUTO_PLUNGE_SETTING]
  const declared =
    setting !== undefined && typeof (setting as { default?: unknown }).default === 'number'
      ? (setting as { default: number }).default
      : AUTO_PLUNGE_SECONDS
  const seconds = Number.isFinite(declared) && declared > 0 ? declared : AUTO_PLUNGE_SECONDS
  // Zero steps means never; `tickAutoPlunger` reads it so.
  if (!(seconds > 0)) return 0
  // `roundHalfAwayFromZero`, not `Math.round`: the C# core has to agree to the step, and
  // `Math.Round` there is banker's rounding. A machine stating 12.5005 s would otherwise wait
  // one step longer in one core than the other, which is a divergence in a validated replay.
  return roundHalfAwayFromZero(seconds * STEP_HZ)
}


